import type { TrainerCapability } from '@zeus/index'
import type { Dependencies, Grants } from '~/pages/backoffice/admin/trainer-capabilities/capability-grants'
import { useMutation, useQuery } from '@apollo/client/react'
import { Loader } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import Spinner from '~/components/Spinner'
import { Button } from '~/components/ui/button'
import { GQL_TRAINER_CAPABILITIES, GQL_UPDATE_TRAINER_CAPABILITIES } from '~/gql/capabilities/capabilities'
import { tourAnchor } from '~/helpers/tour/anchors'
import { useSyncOnChange } from '~/hooks/use-sync-on-change'
import { setGrant } from '~/pages/backoffice/admin/trainer-capabilities/capability-grants'
import CapabilityDomain from '~/pages/backoffice/admin/trainer-capabilities/CapabilityDomain'

/** Le domaine d'une capacité, lu sur son préfixe : `SESSIONS_VIEW` → `sessions`. */
const domainOf = (capability: TrainerCapability) => capability.slice(0, capability.indexOf('_')).toLowerCase()

/**
 * Ce que le rôle Formateur peut faire dans l'organisation (#11). Seul le
 * Formateur se règle ici : un admin ne peut donc pas se retirer ses propres
 * droits. Rien ne s'applique avant « Enregistrer » ; l'API refuse de son côté
 * une capacité accordée sans celles qu'elle suppose.
 */
function TrainerCapabilities() {
  const { t } = useTranslation()
  const { data, loading, error } = useQuery(GQL_TRAINER_CAPABILITIES, { fetchPolicy: 'network-only' })
  const [update, { loading: saving }] = useMutation(GQL_UPDATE_TRAINER_CAPABILITIES)
  const [grants, setGrants] = useState<Grants>({})

  const items = data?.trainerCapabilities ?? []
  const dependencies: Dependencies = Object.fromEntries(items.map(item => [item.capability, item.dependsOn]))
  const domains = [...new Set(items.map(item => domainOf(item.capability)))]

  useSyncOnChange(data?.trainerCapabilities, list =>
    setGrants(Object.fromEntries(list.map(item => [item.capability, item.granted]))))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await update({
        variables: { grants: items.map(({ capability }) => ({ capability, granted: grants[capability] ?? false })) },
      })
      toast.success(t('trainerCapabilities.saved'))
    } catch {
      toast.error(t('trainerCapabilities.saveError'))
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center p-10">
        <Spinner className="size-6" />
      </div>
    )
  }

  // Sans la liste, pas de formulaire : l'enregistrer enverrait des réglages
  // vides, que l'API refuse — mieux vaut dire tout de suite qu'il a manqué.
  if (error || items.length === 0) {
    return <p role="alert" className="p-6 text-center text-sm text-destructive">{t('trainerCapabilities.loadError')}</p>
  }

  return (
    <form onSubmit={handleSubmit} className="flex w-full flex-col gap-6">
      <p className="max-w-2xl text-sm text-muted-foreground" {...tourAnchor('trainerCapabilities.intro')}>
        {t('trainerCapabilities.intro')}
      </p>

      <div className="grid gap-4 md:grid-cols-2" {...tourAnchor('trainerCapabilities.domains')}>
        {domains.map(domain => (
          <CapabilityDomain
            key={domain}
            title={t(`trainerCapabilities.domain.${domain}`)}
            capabilities={items.map(item => item.capability).filter(capability => domainOf(capability) === domain)}
            grants={grants}
            dependencies={dependencies}
            onToggle={(capability, granted) => setGrants(current => setGrant(current, dependencies, capability, granted))}
          />
        ))}
      </div>

      <div className="flex justify-end">
        <Button type="submit" disabled={saving} {...tourAnchor('trainerCapabilities.save')}>
          {saving && <Loader className="mr-2 size-4 animate-spin" />}
          {t('common.save')}
        </Button>
      </div>
    </form>
  )
}

export default TrainerCapabilities
